import Grid from '@mui/material/Grid';
import SortableList from './SortableList';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { ReactComponent as UploadIcon } from '../../../../assets/icons/main/upload-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/main/delete-icon.svg';
import { ReactComponent as SortIcon } from '../../../../assets/icons/main/sort-arrow-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/main/close-icon.svg';
import { lighten, styled, useTheme } from '@mui/material';
import { arrayMove } from '@dnd-kit/sortable';
import get from 'lodash/get';
import lightOrdarkColor from '@check-light-or-dark/color';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const Dnd = ({
  items,
  setItems,
  onChangeValues,
  readOnly,
  ajvErrors,
  onUploadReferenceFiles,
  onDeleteReferenceFiles,
  onDocumentClick,
}: any) => {
  const themeData: any = useTheme();

  return (
    <SortableList
      items={items}
      onChange={setItems}
      renderItem={(item, idx) => (
        <SortableList.Item id={item.id}>
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderTop: `1px solid ${themeData.palette.customBorderColor.main}`,
              backgroundColor:
                idx % 2 === 0
                  ? ''
                  : `${
                      lightOrdarkColor(themeData.palette.background.default) === 'dark'
                        ? lighten(themeData.palette.background.default, 0.05)
                        : lighten(themeData.palette.background.default, 0.9)
                    }`,
              flexWrap: 'nowrap',
            }}
            columnSpacing={4}
          >
            {/* {!readOnly && (
              <Grid item xs={0.5} sx={{ display: 'flex', alignItems: 'center', minWidth: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                  <SortableList.DragHandle />
                </Box>
              </Grid>
            )} */}
            <Grid
              item
              xs={readOnly ? 3 : 2.5}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '150px' }}
            >
              {!readOnly && (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 3, pr: 1 }}>
                  <SortableList.DragHandle />
                </Box>
              )}
              <TextField
                type='text'
                margin='normal'
                fullWidth
                id='activity_name'
                name='activity_name'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeValues(e, item.id)}
                value={item.activity_name}
                disabled={readOnly}
                variant='outlined'
                size='small'
                sx={{ margin: 0, ml: readOnly ? 4 : 0 }}
                helperText={get(ajvErrors, 'instancePath', '').includes(`${idx}/activity_name`) && ajvErrors.message}
                error={get(ajvErrors, 'instancePath', '').includes(`${idx}/activity_name`)}
              />
            </Grid>
            <Grid item xs={readOnly ? 3 : 2} sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
              <TextField
                type='text'
                margin='normal'
                variant='outlined'
                fullWidth
                id='pre_requisites'
                name='pre_requisites'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeValues(e, item.id)}
                value={item.pre_requisites}
                disabled={readOnly}
                size='small'
                sx={{ margin: 0, ml: 1 }}
                helperText={get(ajvErrors, 'instancePath', '').includes(`${idx}/pre_requisites`) && ajvErrors.message}
                error={get(ajvErrors, 'instancePath', '').includes(`${idx}/pre_requisites`)}
              />
            </Grid>
            <Grid item xs={readOnly ? 3 : 2} sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
              <TextField
                type='text'
                margin='normal'
                fullWidth
                variant='outlined'
                id='description'
                name='description'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeValues(e, item.id)}
                value={item.description}
                disabled={readOnly}
                size='small'
                sx={{ margin: 0, ml: '4px' }}
                helperText={get(ajvErrors, 'instancePath', '').includes(`${idx}/description`) && ajvErrors.message}
                error={get(ajvErrors, 'instancePath', '').includes(`${idx}/description`)}
              />
            </Grid>
            <Grid
              item
              xs={readOnly ? 3 : 3.5}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                minWidth: '300px',
                pl: 0
              }}
            >
              {!readOnly && (
                <Button
                  component='label'
                  startIcon={<UploadIcon style={{ color: themeData.palette.custom.light }} />}
                  sx={{
                    minWidth: '30px',
                    p: 0,
                    width: !(item.documents || []).length ? '220px' : '30px',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <VisuallyHiddenInput
                    type='file'
                    onChange={(e) => onUploadReferenceFiles(e, item.id)}
                    onClick={(event: any) => {
                      event.target.value = null;
                    }}
                  />
                  {!(item.documents || []).length && (
                    <Typography fontWeight={400} fontSize={16} color={themeData.palette.custom.light} textTransform="none">
                      Browse File & Upload
                    </Typography>
                  )}
                </Button>
              )}
              <Box
                sx={{
                  height: '60px',
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {(item.documents || []).map((document, idx) => {
                  if (document) {
                    const docName = typeof document === 'string' ? document : get(document, 'name', 'file');
                    let doc = docName;
                    let isLink = false;
                    if (doc.includes('http')) {
                      const splittedDoc = doc.split('/');
                      doc = splittedDoc[splittedDoc.length - 1];
                      isLink = true;
                    }
                    return (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        {isLink ? (
                          <Typography
                            fontWeight={400}
                            fontSize={16}
                            color='primary'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onDocumentClick(docName, item.activity_id)}
                          >
                            {doc}
                          </Typography>
                        ) : (
                          <Typography fontWeight={400} fontSize={16} color='primary'>
                            {doc}
                          </Typography>
                        )}
                        {!readOnly && (
                          <IconButton sx={{ p: '4px', ml: 1 }} onClick={() => onDeleteReferenceFiles(docName, item.id)}>
                            <CloseIcon
                              style={{ width: '16px', height: '16px', color: themeData.palette.custom.light }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    );
                  } else {
                    return '';
                  }
                })}
              </Box>
            </Grid>
            {!readOnly && (
              <Grid
                item
                xs={2}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, minWidth: '200px' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box display='flex' justifyContent='center' sx={{ width: '160px' }}>
                    <IconButton
                      onClick={() => {
                        setItems(items.filter((ele: any) => ele.id !== item.id));
                      }}
                      disabled={readOnly}
                    >
                      <DeleteIcon style={{ color: themeData.palette.custom.light }} />
                    </IconButton>
                  </Box>
                  <Box
                    display='flex'
                    justifyContent='center'
                    sx={{
                      paddingBlock: '14.5px',
                      paddingInline: 3,
                      borderLeft: `1px solid ${themeData.palette.customBorderColor.main}`,
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        const overIndex = idx - 1;
                        if (overIndex > -1) {
                          setItems(arrayMove(items, idx, overIndex));
                        }
                      }}
                      disabled={readOnly}
                    >
                      <SortIcon style={{ rotate: '180deg', color: themeData.palette.custom.light }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        const overIndex = idx + 1;
                        if (overIndex < items.length) {
                          setItems(arrayMove(items, idx, overIndex));
                        }
                      }}
                      disabled={readOnly}
                    >
                      <SortIcon style={{ color: themeData.palette.custom.light }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </SortableList.Item>
      )}
    />
  );
};

export default Dnd;
